import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { COMBO_DEFS } from '../data/comboDefs';
import { useCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { getProductImageUrl } from '../utils/productImage';
import api from '../api/axios';

const C = { primary: '#8B1A1A', gold: '#D4A017' };

export default function ComboDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { addToast } = useToast();
  const { isMobile } = useBreakpoint();

  const combo = COMBO_DEFS.find(c => c.slug === slug);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    if (!combo) { navigate('/shop'); return; }

    Promise.all(
      combo.categories.map(cat =>
        api.get(`/products/?category=${cat}&page_size=1`)
          .then(res => (res.data.results || res.data)[0])
          .catch(() => null)
      )
    ).then(results => {
      setProducts(results.filter(Boolean));
      setLoading(false);
    });
  }, [slug, combo, navigate]);

  if (!combo) return null;

  const totalIndividual = products.reduce(
    (sum, p) => sum + Math.round(parseFloat(p.effective_price || p.price)),
    0
  );
  const savings = Math.round(totalIndividual * 0.2);
  const comboPrice = totalIndividual - savings;

  const handleAddAll = () => {
    if (!products.length) return;
    products.forEach(p => addToCart(p, 1));
    setAdded(true);
    addToast(`${combo.name} added to cart!`, 'success');
    setTimeout(() => setAdded(false), 2500);
  };

  const fallback = (name) =>
    `https://placehold.co/400x480/8B1A1A/gold?text=${encodeURIComponent(name.slice(0, 15))}`;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '24px 16px' : '40px 24px' }}>
      {/* Breadcrumb */}
      <p style={{ color: '#999', fontSize: 13, marginBottom: 20 }}>
        <span onClick={() => navigate('/')} style={{ cursor: 'pointer', color: C.primary }}>Home</span>
        {' / '}
        <span onClick={() => navigate('/')} style={{ cursor: 'pointer', color: C.primary }}>Combos</span>
        {' / '}
        {combo.name}
      </p>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: isMobile ? 28 : 44 }}>
        <span style={{ background: combo.badgeColor, color: '#fff', fontSize: 12, fontWeight: 700, padding: '4px 14px', borderRadius: 12 }}>
          {combo.occasion}
        </span>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: isMobile ? 26 : 36, color: '#2C2C2C', margin: '12px 0 8px', lineHeight: 1.25 }}>
          {combo.name}
        </h1>
        <p style={{ color: '#777', fontSize: 15, margin: 0 }}>{combo.tagline}</p>
      </div>

      {/* Products grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#888', fontSize: 16 }}>Loading bundle…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? 16 : 24, marginBottom: 36 }}>
          {products.map((product, i) => {
            const imgSrc = getProductImageUrl(product, fallback(product.name));
            const price = Math.round(parseFloat(product.effective_price || product.price));
            const origPrice = product.sale_price ? Math.round(parseFloat(product.price)) : null;
            return (
              <div
                key={product.id}
                style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 14px rgba(0,0,0,0.08)', border: '1px solid #f0e0e0' }}
              >
                <Link to={`/product/${product.slug}`} style={{ textDecoration: 'none' }}>
                  <img
                    src={imgSrc}
                    alt={product.name}
                    style={{ width: '100%', height: isMobile ? 200 : 240, objectFit: 'cover', display: 'block' }}
                    onError={e => { e.target.src = fallback(product.name); }}
                  />
                </Link>
                <div style={{ padding: '14px' }}>
                  <p style={{ fontSize: 11, color: '#999', margin: '0 0 4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {combo.categoryLabels[i]}
                  </p>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 15, color: '#2C2C2C', margin: '0 0 8px', lineHeight: 1.4 }}>
                    {product.name}
                  </h3>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: 17, fontWeight: 700, color: C.primary }}>₹{price.toLocaleString('en-IN')}</span>
                    {origPrice && (
                      <span style={{ fontSize: 13, color: '#999', textDecoration: 'line-through' }}>₹{origPrice.toLocaleString('en-IN')}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Bundle CTA */}
      {!loading && products.length > 0 && (
        <div style={{
          background: '#FFF8F0',
          border: '1.5px solid #e0d0d0',
          borderRadius: 12,
          padding: isMobile ? '20px 16px' : '28px 36px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: 20,
        }}>
          <div>
            <p style={{ fontSize: 13, color: '#777', margin: '0 0 6px' }}>Bundle Deal — {products.length} pieces</p>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: isMobile ? 28 : 34, fontWeight: 800, color: C.primary }}>
                ₹{comboPrice.toLocaleString('en-IN')}
              </span>
              <span style={{ fontSize: 18, color: '#bbb', textDecoration: 'line-through' }}>
                ₹{totalIndividual.toLocaleString('en-IN')}
              </span>
              <span style={{ background: '#e8f5e9', color: '#2e7d32', fontSize: 13, fontWeight: 700, padding: '3px 10px', borderRadius: 4 }}>
                Save ₹{savings.toLocaleString('en-IN')} (20% off)
              </span>
            </div>
            <p style={{ fontSize: 12, color: '#aaa', margin: '8px 0 0' }}>
              Items added at their current individual prices. Discount applied at checkout with code <strong>COMBO20</strong>.
            </p>
          </div>

          <button
            onClick={handleAddAll}
            style={{
              minWidth: isMobile ? '100%' : 220,
              padding: '15px 28px',
              background: added ? '#2e7d32' : C.primary,
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              letterSpacing: 0.4,
              transition: 'background 0.3s',
              flexShrink: 0,
            }}
          >
            {added ? '✓ All Added to Cart!' : 'Add All to Cart'}
          </button>
        </div>
      )}

      {/* Included categories list */}
      {!loading && (
        <div style={{ marginTop: 28, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
          {[
            { icon: '🚚', text: 'Free Shipping above ₹999' },
            { icon: '💵', text: 'COD Available' },
            { icon: '↩', text: '7-Day Easy Returns' },
          ].map(b => (
            <span key={b.text} style={{ background: '#fff', border: '1px solid #e0d0d0', borderRadius: 20, padding: '6px 14px', fontSize: 12, color: '#555', fontWeight: 500 }}>
              {b.icon} {b.text}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
